import * as React from 'react';
import styled from 'styled-components';
import {
  Flex,
  Text,
  TouchLink,
  TouchLinkGroup,
  useIsMobile,
} from '@amzn/storm-ui';
import { Account, AdvertisingAccountType } from '@amzn/aac-chrome-internal-api';
import {
  AccountTypeLabel,
  AccountTypeLabelContainer,
  IA2AccountDropdownList,
  IA2AccountListItem,
} from './styles';
import { IA2AdvertisingAccountItem } from './IA2AdvertisingAccountItem';
import { IA2MobileAccountListItem } from './IA2MobileAccountListItem';
import { NoAccountsMessage } from './NoAccountsMessage';
import { ErrorLoadingAccountsMessage } from './ErrorLoadingAccountsMessage';
import { InfiniteScrollSentinel } from './InfiniteScrollSentinel';
import { getCountryLabel } from './getCountryLabel';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getAccountTypeListLabel } from './AccountTypeLocalizationKeyMapping';
import { getTaktAttributes, logWithTakt } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { AccountDataSource } from './useAccountData';

interface AccountFlatListProps {
  dataSource: AccountDataSource;
  selectedEntityId?: string;
  /** Scroll container used as the pagination prefetch root. */
  scrollRootRef?: React.RefObject<HTMLElement>;
}

const LoadingAccountsContainer = styled.div`
  display: flex;
  padding: ${({ theme }) =>
    `${theme.spacing.xsmall.default} ${theme.spacing.base.default}`};
  justify-content: center;
`;

/**
 * Unified account list: a single flat sequence of accounts with each
 * account-type header rendered as a visual divider (no per-group "Show 30 more"
 * pagination). Loading more happens automatically via {@link InfiniteScrollSentinel}
 * — identical UX for whale (server-paged) and non-whale (local) sources, which
 * the {@link AccountDataSource} abstraction hides. Each row renders the same
 * secondary country/marketplace label as the legacy switcher via
 * {@link getCountryLabel} (per-type: Manager/DSP-entity → none; multi-country →
 * "Multiple countries"; single-country → localized country name).
 */
export const AccountFlatList: React.VFC<AccountFlatListProps> = ({
  dataSource,
  selectedEntityId,
  scrollRootRef,
}) => {
  const bundle = useAccountDropdownBundle();
  const isMobile = useIsMobile();
  const { accounts, hasMore, loadMore, isLoading, error, retry } = dataSource;

  const isEmpty =
    accounts.length === 0 ||
    accounts.every((group) => group.accounts.length === 0);

  // Only take over the whole panel with the error message when there is nothing
  // to show. If a loadMore/search fails but we still hold prior accounts, keep
  // rendering them (the hook retains last-good data) so one paginated-page
  // failure never blanks the switcher.
  if (error && isEmpty) {
    return <ErrorLoadingAccountsMessage />;
  }

  if (isEmpty && !isLoading) {
    return <NoAccountsMessage />;
  }

  // Emitted on account selection: the account's type and its zero-based
  // position in the flattened list (across all group boundaries), so the
  // funnel can measure how far users scroll before switching.
  const logSelect = (type: AdvertisingAccountType, position: number) => {
    logWithTakt('ccx-account-switcher-select', { accountType: type, position });
  };

  const renderAccount = (
    account: Account,
    type: AdvertisingAccountType,
    position: number,
  ) => {
    const isSelected = selectedEntityId === account.entityId;
    // Same secondary label as the legacy switcher: getCountryLabel decides per
    // type (Manager/DSP-entity → none; multi-country → "Multiple countries";
    // single-country → localized country name).
    const countryLabel = getCountryLabel({
      accountType: type,
      account,
      bundle,
    });

    if (isMobile) {
      return (
        <TouchLink
          key={account.url}
          href={account.url}
          data-ccx-e2e-id="aac-account-link"
          onClick={() => logSelect(type, position)}
          {...getTaktAttributes('aac-account-link', chromeComponents.HEADER, {
            url: account.url,
          })}
        >
          <IA2MobileAccountListItem
            accountLabel={account.label}
            countryLabel={countryLabel}
            retailerName={account.retailerName}
          />
        </TouchLink>
      );
    }

    return (
      <IA2AccountListItem
        key={account.url}
        isSelected={isSelected}
        onClick={() => logSelect(type, position)}
      >
        <IA2AdvertisingAccountItem
          isSelected={isSelected}
          advertisingAccount={account}
          retailerName={account.retailerName}
          countryLabel={countryLabel}
        />
      </IA2AccountListItem>
    );
  };

  const visibleAccountCount = accounts.reduce(
    (count, group) => count + group.accounts.length,
    0,
  );
  let flatIndex = 0;
  return (
    <Flex direction="column" alignItems="normal">
      {accounts.map((group) => {
        if (group.accounts.length === 0) {
          return null;
        }
        const items = group.accounts.map((account) =>
          renderAccount(account, group.accountType, flatIndex++),
        );
        return (
          <Flex key={group.label} direction="column" alignItems="normal">
            <AccountTypeLabelContainer>
              <AccountTypeLabel weight="bold" withMargin={!isMobile}>
                {getAccountTypeListLabel(group.accountType, bundle)}
              </AccountTypeLabel>
            </AccountTypeLabelContainer>
            {isMobile ? (
              <TouchLinkGroup>{items}</TouchLinkGroup>
            ) : (
              <IA2AccountDropdownList>{items}</IA2AccountDropdownList>
            )}
          </Flex>
        );
      })}
      <InfiniteScrollSentinel
        onVisible={loadMore}
        enabled={hasMore && !isLoading && !error}
        contentVersion={visibleAccountCount}
        root={scrollRootRef}
      />
      {isLoading && (
        <LoadingAccountsContainer
          data-testid="account-list-loading"
          role="status"
        >
          <Text color="secondary" size="xsmall">
            {bundle.getMessage('account-dropdown-loading')}
          </Text>
        </LoadingAccountsContainer>
      )}
      {error && !isLoading && (
        // A page fetch failed but prior accounts are still shown. The sentinel is
        // disabled above so it can't auto-retry into a tight failure loop; offer an
        // explicit retry instead.
        <TouchLink onClick={retry}>
          {bundle.getMessage('account-dropdown-load-more-retry')}
        </TouchLink>
      )}
    </Flex>
  );
};
